import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../../lib/api';
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle } from 'lucide-react';

const contactSchema = z.object({
  name: z.string().min(1, 'Please provide your name'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  subject: z.string().min(1, 'Please enter a subject'),
  enquiryType: z.enum(['GENERAL', 'PUBLISHING', 'AUTHOR', 'SUPPORT']),
  message: z.string().min(10, 'Message must be at least 10 characters long'),
});

type ContactFormData = z.infer<typeof contactSchema>;

export const ContactPage: React.FC = () => {
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      enquiryType: 'GENERAL',
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await api.post('/public/contact', data);
      if (res.data?.success) {
        setSubmittedRef(res.data.data.referenceNo);
        reset();
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error?.message || 'Failed to submit contact request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-16 py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
          Contact Editorial &amp; Support
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-editorial-dark">
          Connect with For The Truth Publishing
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
          Whether you are an author with questions regarding submission, an institution seeking permissions, or a current client needing assistance, our staff is here to help.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        
        {/* Contact Info Sidebar */}
        <div className="lg:col-span-5 space-y-8">
          <div className="p-8 rounded-2xl bg-editorial-dark text-white space-y-6">
            <h3 className="font-serif text-2xl font-bold">Editorial Offices</h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-light">
              For The Truth Publishing coordinates acquisitions, editorial passes, and production management across our global network of scholars and editors.
            </p>

            <div className="space-y-4 text-xs sm:text-sm pt-4 border-t border-slate-800">
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                <div>
                  <span className="text-slate-400 block text-[11px]">Write To Us (Email)</span>
                  <a href="mailto:contact@forthetruth.in" className="text-white font-medium hover:text-brand-300 transition-colors">
                    contact@forthetruth.in
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                <div>
                  <span className="text-slate-400 block text-[11px]">Phone / WhatsApp</span>
                  <a href="tel:+918604685533" className="text-white font-medium hover:text-brand-300 transition-colors">
                    +91 86046 85533
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-white border border-editorial-border shadow-xs text-xs text-slate-600 space-y-2">
            <span className="font-bold text-slate-900 uppercase text-[11px] block">Looking to Submit a Manuscript?</span>
            <p>For complete publishing proposals, please use our comprehensive Publishing Enquiry System rather than the general contact form.</p>
            <a href="/submit-enquiry" className="font-bold text-brand-700 hover:underline block pt-1">
              Go to Manuscript Submission Form &rarr;
            </a>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-7">
          <div className="p-8 sm:p-10 rounded-2xl bg-white border border-editorial-border shadow-xs">
            
            {submittedRef ? (
              <div className="py-12 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900">Message Received</h3>
                <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
                  Thank you for contacting For The Truth Publishing. Your message has been logged under reference number:
                </p>
                <div className="inline-block px-4 py-2 bg-slate-100 rounded-lg text-brand-800 font-mono font-bold text-sm">
                  {submittedRef}
                </div>
                <div className="pt-4">
                  <button
                    onClick={() => setSubmittedRef(null)}
                    className="px-6 py-2 rounded-lg bg-editorial-dark text-white text-xs font-semibold hover:bg-slate-800"
                  >
                    Send Another Message
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <h3 className="font-serif text-2xl font-bold text-slate-900">Send an Editorial Message</h3>
                  <p className="text-xs text-slate-500 mt-1">Please fill in the details below. We typically reply within 1–2 business days.</p>
                </div>

                {errorMessage && (
                  <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      {...register('name')}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                      placeholder="e.g. Dr. Arthur Pendleton"
                    />
                    {errors.name && <p className="text-rose-600 text-[11px] mt-1">{errors.name.message}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                      placeholder="author@example.com"
                    />
                    {errors.email && <p className="text-rose-600 text-[11px] mt-1">{errors.email.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Enquiry Category
                    </label>
                    <select
                      {...register('enquiryType')}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden bg-white"
                    >
                      <option value="GENERAL">General Inquiries</option>
                      <option value="PUBLISHING">Publishing Services / RFQ</option>
                      <option value="AUTHOR">Author Submission Support</option>
                      <option value="SUPPORT">Contracted Client Support</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    {...register('subject')}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                    placeholder="e.g. Inquiry regarding theological monograph assessment"
                  />
                  {errors.subject && <p className="text-rose-600 text-[11px] mt-1">{errors.subject.message}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Message *
                  </label>
                  <textarea
                    rows={5}
                    {...register('message')}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                    placeholder="Please provide details regarding your inquiry..."
                  />
                  {errors.message && <p className="text-rose-600 text-[11px] mt-1">{errors.message.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span>Submitting Message...</span>
                  ) : (
                    <>
                      <span>Transmit Message to Editorial Team</span>
                      <Send className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </form>
            )}

          </div>
        </div>

      </div>

    </div>
  );
};
